// Shared pure helpers and visual constants extracted from
// `pages/CustomerDetailPage.js` as part of the incremental modularization
// (P2 refactor). Kept intentionally free of React state, effects and API
// calls so any consumer — the current page, upcoming tab / modal
// sub-components, or unit tests — can import them without pulling in the
// whole customer-detail dependency graph.
//
// Nothing here touches functionality or styling. This file is a
// 1:1 move of the top-level helpers/constants from the page.
import React from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Tiny inline validation hint shared by the document & IBAN edit fields.
 * Empty input → no message (so optional fields don't show false errors).
 * Valid → green check + "Válido". Invalid → red text with the rule message.
 */
export const ValidationHint = ({ result }) => {
  if (!result || !result.normalized) return null;
  return result.valid ? (
    <div className="flex items-center gap-1 mt-1 text-xs text-emerald-400">
      <CheckCircle2 className="h-3 w-3" />
      <span>Válido</span>
    </div>
  ) : (
    <div className="flex items-center gap-1 mt-1 text-xs text-rose-400">
      <AlertCircle className="h-3 w-3" />
      <span>{result.error}</span>
    </div>
  );
};

export const inputBorderForValidation = (result) => {
  if (!result || !result.normalized) return "border-[#1E1E5F]";
  return result.valid ? "border-emerald-500/60" : "border-rose-500/70";
};

// Format date to Spanish format dd/mm/yyyy
export const formatDateES = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Format date for input (yyyy-mm-dd)
export const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

// Helper for numeric inputs - removes leading zeros
export const formatNumericValue = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return "";
  if (num === 0) return "";
  return num.toString();
};

// Calculate days remaining between today and a future date
export const calculateDaysRemaining = (endDateStr) => {
  if (!endDateStr) return null;
  const endDate = new Date(endDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  const diffTime = endDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : null;
};

// Calculate end date based on start date and months
export const calculateEndDate = (startDateStr, months) => {
  if (!startDateStr || !months) return null;
  const startDate = new Date(startDateStr);
  if (isNaN(startDate.getTime())) return null;
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + parseInt(months));
  return endDate.toISOString().split("T")[0];
};

export const statusLabels = {
  ACTIVO: "Activo",
  LEAD: "Lead",
  BAJA: "Baja",
  IMPAGO: "Impago",
};

export const statusColors = {
  ACTIVO: "bg-[#42E695]/20 text-[#42E695] border-[#42E695]/30",
  LEAD: "bg-[#03BEE4]/20 text-[#03BEE4] border-[#03BEE4]/30",
  BAJA: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  IMPAGO: "bg-red-500/20 text-red-400 border-red-500/30",
};

export const serviceStatusColors = {
  ACTIVO: "bg-[#42E695]/20 text-[#42E695]",
  PENDIENTE: "bg-yellow-500/20 text-yellow-400",
  SUSPENDIDO: "bg-orange-500/20 text-orange-400",
  BAJA: "bg-red-500/20 text-red-400",
};

export const discountTypes = [
  { value: "PROMOCIÓN", label: "Promoción" },
  { value: "FIDELIDAD", label: "Fidelidad" },
  { value: "VOLUMEN", label: "Volumen" },
  { value: "COMERCIAL", label: "Comercial" },
  { value: "OTROS", label: "Otros" },
];
